/**
 * Standing rule: an age group, grad year, grade or gender word must never be
 * stored in a team name anywhere in the product.
 *
 * This wraps parseTeamName so every entry point (registration, the Live Sheet
 * "Add teams" form, pasted lists, the team editor) strips those tokens out of
 * the name and turns them into division/gender fields instead.
 */
import {
  extractGender,
  normalizeForMatch,
  parseTeamName,
  stripGenderWords,
} from "@/lib/teamNameParser";

export interface CleanedTeamName {
  /** The name to store — age/grad year/grade and gender token removed. */
  name: string;
  /** "17u" style label worked out from the typed text, if any. */
  ageGroup: string | null;
  /** "girls" | "boys" when the text carried a gender token. */
  gender: "girls" | "boys" | null;
  /** Human summary, e.g. "TNT · Girls 14u". */
  summary: string;
  /** True when anything was stripped or inferred. */
  changed: boolean;
}

const GIRLS_RE = /\b(girls?|womens?|women|ladies)\b/i;
const BOYS_RE = /\b(boys?|mens?|men)\b/i;

/** Join club + variation into a storable name, gender words removed. */
export function buildDisplayName(club: string, variation?: string | null) {
  return stripGenderWords(
    [club, variation].filter(Boolean).join(" ").replace(/\s+/g, " ").trim()
  );
}



export function cleanTeamName(
  raw: string,
  opts?: { eventYear?: number; existingClubs?: string[] }
): CleanedTeamName {
  const input = (raw ?? "").trim();
  if (!input) {
    return { name: "", ageGroup: null, gender: null, summary: "", changed: false };
  }

  const parsed = parseTeamName(input, {
    eventYear: opts?.eventYear ?? new Date().getFullYear(),
    existingClubs: opts?.existingClubs ?? [],
  });

  const gender = parsed.gender ?? extractGender(input);

  // Never fall back to the raw input — that's how a gender word got stored.
  const name =
    buildDisplayName(parsed.club, parsed.variation) || stripGenderWords(input);

  const genderWord = gender === "girls" ? "Girls" : gender === "boys" ? "Boys" : "";
  const divisionText = [genderWord, parsed.ageGroup].filter(Boolean).join(" ");

  return {
    name,
    ageGroup: parsed.ageGroup,
    gender,
    summary: [name, divisionText].filter(Boolean).join(" · "),
    changed: normalizeForMatch(name) !== normalizeForMatch(input),
  };
}

/**
 * Pick the division option that matches an inferred age group + gender.
 * Exact age match only — we never substitute a nearby age group.
 */
export function matchDivisionLabel(
  labels: string[],
  ageGroup: string | null,
  gender: "girls" | "boys" | null
): string | null {
  if (!ageGroup) return null;
  const age = normalizeForMatch(ageGroup);
  const hits = labels.filter((l) => {
    const n = normalizeForMatch(l);
    return n === age || n.split(" ").includes(age) || n.replace(/\s+/g, "").includes(age);
  });
  if (hits.length === 0) return null;
  if (gender) {
    const re = gender === "girls" ? GIRLS_RE : BOYS_RE;
    const g = hits.find((l) => re.test(l));
    if (g) return g;
  }
  const plain = hits.find((l) => !GIRLS_RE.test(l) && !BOYS_RE.test(l));
  return plain ?? hits[0];
}
